import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Bot, BarChart3, ShoppingCart, Globe, Zap, Star, ExternalLink } from "lucide-react";
import { LiveDemosSection } from "@/components/sections/LiveDemosSection";

const VERTICALS = [
  { key: "hotel", label: "Resorts & Homestays", line: "Direct Bookings • AI Assistant" },
  { key: "restaurant", label: "Restaurants", line: "Menu • Ordering • Reservations" },
  { key: "doctor", label: "Clinics & Ayurveda", line: "Appointments • Local Search" },
  { key: "retail", label: "Shops & Spice Exporters", line: "Online Store • Payments" },
  { key: "school", label: "Schools & Colleges", line: "Admissions • Alumni • Events" },
  { key: "realtor", label: "Realtors & Builders", line: "Listings • Leads • Tours" },
];

const DEMO_BY_VERTICAL = {
  restaurant: "/demos/restaurant", hotel: "/demos/hotel", doctor: "/demos/medical",
  school: "/demos/college", retail: "/demos/fresh", realtor: "/demos/real-estate",
};

const PILLARS = [
  { icon: Globe, t: "Websites", d: "New sites, redesigns & web apps built mobile-first, in Malayalam & English." },
  { icon: Bot, t: "AI & Automation", d: "AI assistants, lead qualification & workflow automation." },
  { icon: BarChart3, t: "Analytics", d: "Traffic, conversions & AI-generated insights." },
  { icon: Search, t: "SEO & AI Search", d: "Rank on Google and be found by AI search." },
  { icon: ShoppingCart, t: "E-Commerce", d: "Sell online with UPI, cards & catalog." },
  { icon: Zap, t: "Care & Growth", d: "Hosting, security, maintenance & monthly growth." },
];

export default function KeralaHome() {
  useEffect(() => { document.title = "Kerala.com · Digital Growth Partner for Kerala"; }, []);
  return (
    <div data-testid="kerala-home">
      <section className="bg-gradient-to-br from-[#024E3F] to-[#012a22] text-white py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1.5 mb-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-200">God's Own Country · Online</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-3xl">
            Grow your business online, <span className="text-emerald-300">the Kerala way</span>
          </h1>
          <p className="text-emerald-100/80 text-base md:text-lg mt-6 max-w-2xl">Websites • AI • Analytics • SEO • E-Commerce • Automation — for resorts, restaurants, clinics, shops and exporters across Kerala and India. Priced in ₹, GST invoicing, UPI, WhatsApp.</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/kerala/audit" data-testid="kerala-hero-audit" className="bg-white text-[#024E3F] font-semibold rounded-xl px-6 py-3.5 hover:-translate-y-0.5 transition-transform inline-flex items-center gap-2">Free Website Health Check <ArrowRight size={17} /></Link>
            <Link to="/kerala/pricing" data-testid="kerala-hero-pricing" className="bg-white/10 border border-white/20 rounded-xl px-6 py-3.5 hover:bg-white/20 transition-colors">See ₹ Pricing</Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">Everything to grow online</h2>
          <p className="text-slate-500 mt-3 md:text-lg max-w-2xl">One local partner for your website and everything around it — from first launch to long-term growth.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {PILLARS.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-6 hover:border-emerald-300 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-4"><p.icon size={20} className="text-emerald-700" /></div>
                <h3 className="font-heading text-base font-bold text-slate-900">{p.t}</h3>
                <p className="text-sm text-slate-500 mt-1.5">{p.d}</p>
              </motion.div>
            ))}
          </div>
          <Link to="/kerala/services" className="inline-flex items-center gap-2 text-emerald-700 font-semibold mt-8 hover:gap-3 transition-all">Explore all services <ArrowRight size={16} /></Link>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">Built for Kerala businesses</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {VERTICALS.map((v) => (
              <div key={v.key} data-testid={`kerala-vertical-${v.key}`}
                className="group bg-white border border-slate-200 rounded-2xl p-5 card-shadow hover:-translate-y-1 transition-transform flex flex-col">
                <div className="flex items-center gap-2 mb-1"><Star size={15} className="text-amber-400" /><h3 className="font-heading font-bold text-slate-900">{v.label}</h3></div>
                <p className="text-sm text-slate-500">{v.line}</p>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <Link to={`/kerala/contact?vertical=${v.key}`} data-testid={`kerala-vertical-start-${v.key}`} className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1 hover:gap-2 transition-all">Get started <ArrowRight size={13} /></Link>
                  {DEMO_BY_VERTICAL[v.key] && (
                    <a href={DEMO_BY_VERTICAL[v.key]} target="_blank" rel="noopener noreferrer" data-testid={`kerala-vertical-demo-${v.key}`} className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1 hover:text-slate-900 transition-colors">View live demo <ExternalLink size={12} /></a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LiveDemosSection accent="#024E3F"
        subtitle="Real, browsable websites we've built for resorts, restaurants, clinics, shops and more — every one with a smart AI Assistant that answers guest questions and captures bookings in Malayalam & English." />

      <section className="py-16 bg-[#024E3F] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold">Ready to grow your business online?</h2>
          <p className="text-emerald-100/80 mt-3">Start with a free website health check — get your score and an AI action plan.</p>
          <Link to="/kerala/audit" className="mt-8 inline-flex items-center gap-2 bg-white text-[#024E3F] font-semibold rounded-xl px-7 py-4 hover:-translate-y-0.5 transition-transform">Run my free health check <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
